import { booleanAttribute, Component, computed, ElementRef, inject, input, output, signal, TemplateRef, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, of, switchMap } from 'rxjs';
import { isBlank } from '../../../../../core/utils/his-string.utils';
import { HisUiService } from '../../../../../core/service/base/his-ui.service';
import { HisUploadFile } from '../../../../model/his-upload-file';
import { HisUploadFileDownloadComponent } from '../../../doc-download/his-upload-file-download.component';
import { HisSelectFile, hisSelectFileKey, hisSelectFileName } from '../../model/his-select-file.model';
import { LocalMedia, documentMetadataObjGuidOf } from '../../model/local-media.model';
import { UploadSourceType } from '../../model/upload-source-type.model';
import { ResourcePropertyDetail } from '../../model/resource-property-detail.model';
import { HisSelectFilePropertyTemplateContext } from '../../model/his-select-file-property-template-context.model';
import { HisResourcePropertyDisplayService } from '../../service/his-resource-property-display.service';
import { HisSelectFileService } from '../../service/his-select-file.service';

/**
 * Displays a single {@link HisSelectFile} (its filename, download link and
 * — when enabled — its resource properties) and offers a remove action.
 *
 * Rendered as the {@code <dd>} of one name-value group of the definition list the parent
 * {@code HisSelectFileComponent} spans over the whole selection — exactly like the JSF
 * {@code <his:selectFile />} does: the filename lives in a {@code <dt>}, which the parent
 * renders, the actions in the {@code <dd>}, which is the host of this component. A
 * {@code <dl>} may only contain {@code <dt>}/{@code <dd>} elements, which is why this
 * component is an attribute of such a {@code <dd>} instead of an element of its own.
 *
 * The resource properties are fetched per file and cached in a signal; they are
 * only recomputed when the underlying file (identified via {@link hisSelectFileKey})
 * changes. The parent {@code HisSelectFileComponent} renders one instance per array
 * element, so Angular's {@code @for} tracking keeps each item — and therefore its
 * {@link resourceProperties} signal — stable across change detection cycles.
 */
@Component( {
    standalone: false,
    // eslint-disable-next-line @angular-eslint/component-selector -- ein <dl> laesst kein eigenes Element zwischen sich und seinen <dt>/<dd> zu
    selector: 'dd[his-select-file-item]',
    templateUrl: './his-select-file-item.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './his-select-file-item.component.css',
} )
export class HisSelectFileItemComponent {

    // Constants
    private readonly TRANSLATION_COMMENT_ADD = 'common.components.selectFile.addCommentOf';
    private readonly TRANSLATION_COMMENT_EDIT = 'common.components.selectFile.editCommentOf';
    private readonly TRANSLATION_COMMENT_SHOW = 'common.components.selectFile.showCommentOf';
    private readonly TRANSLATION_COMMENT_OVERLAY_TITLE = 'common.components.selectFile.editCommentOverlay.title';

    /** The file to display. */
    readonly file = input.required<HisSelectFile>();

    /** Upload field configuration key, shared with the parent component. */
    readonly configKey = input.required<string>();

    /**
     * The local-media item the {@link file} refers to, resolved by the parent component.
     * Only set for a selection with source type {@code localMedia}, and only as long as
     * the selection has not been persisted — afterwards the server delivers the file as
     * a regular upload.
     */
    readonly localMedia = input<LocalMedia>();

    /** Show the remove action in a disabled state. */
    readonly disabled = input( false, { transform: booleanAttribute } );

    /** Show the item in a readonly state (remove action disabled). */
    readonly readonly = input( false, { transform: booleanAttribute } );

    /** Whether resource (process) properties should be fetched and rendered. */
    readonly processProperties = input( false, { transform: booleanAttribute } );

    /**
     * Whether the upload configuration allows an individual comment for this file.
     * Provided by the parent {@code HisSelectFileComponent}.
     */
    readonly allowComment = input( false, { transform: booleanAttribute } );

    /** Whether the download action is unavailable for this file. */
    readonly disableDownload = input( false, { transform: booleanAttribute } );

    /**
     * Optional template projected by the parent {@code HisSelectFileComponent}.
     * When set, it is rendered between the download link and the action buttons.
     * The current {@link HisSelectFile} is provided as the template's implicit
     * context (accessible via {@code let-file}), and the {@link customProperties}
     * map is provided under the {@code properties} context key (accessible via
     * {@code let-properties="properties"}).
     */
    readonly propertyCustomDisplayIcon = input<TemplateRef<HisSelectFilePropertyTemplateContext>>();

    /**
     * Optional template projected by the parent {@code HisSelectFileComponent}.
     * When set, it is rendered inside the resource-properties overlay panel,
     * after the {@link defaultProperties}. The current {@link HisSelectFile} is
     * provided as the template's implicit context (accessible via {@code let-file}),
     * and the {@link customProperties} map under the {@code properties} context key
     * (accessible via {@code let-properties="properties"}).
     */
    readonly propertyCustomDisplay = input<TemplateRef<HisSelectFilePropertyTemplateContext>>();

    /**
     * Property category keys ({@link ResourcePropertyDetail.categoryKey}) whose
     * properties are treated as "custom": they are excluded from
     * {@link defaultProperties} (and therefore the overlay panel) and instead
     * grouped into {@link customProperties}.
     */
    readonly propertyCustomDisplayCategories = input<string[]>( [] );

    /** Emitted when the user requests removal of this file. */
    readonly remove = output<void>();

    /** Emitted with the new comment when the user applies the comment dialog. */
    readonly commentChange = output<string>();

    private readonly selectFileService = inject( HisSelectFileService );
    private readonly displays = inject( HisResourcePropertyDisplayService );
    private readonly hisUiService = inject( HisUiService );
    private readonly host = inject<ElementRef<HTMLElement>>( ElementRef );

    /** The download link of this file, absent while the file offers no download. */
    private readonly download = viewChild( HisUploadFileDownloadComponent );

    /** The filename derived from the file, or `null` if none can be determined. */
    readonly filename = computed<string | null>( () => hisSelectFileName( this.file(), this.localMedia() ) );

    /**
     * The upload the download link is rendered for, or `null` when this item offers no download.
     *
     * A local-media selection is not backed by an upload, so it is represented by a stand-in
     * {@link HisUploadFile} carrying just the data the download needs: its document-metadata
     * guid — an unprefixed media reference <em>is</em> that guid, while media that is not backed
     * by document metadata carries a prefixed reference and therefore has no download.
     */
    protected readonly downloadFile = computed<HisUploadFile | null>( () => {
        const file = this.file();
        if ( file?.upload ) {
            return file.upload;
        }

        const documentMetadataObjGuid = documentMetadataObjGuidOf( file?.localMedia?.mediaReference );
        if ( documentMetadataObjGuid == null ) {
            return null;
        }

        return Object.assign( new HisUploadFile(), {
            uuid: documentMetadataObjGuid,
            name: this.filename(),
        } );
    } );

    /**
     * `true` when the file carries everything the server needs to resolve it to a resource:
     * an upload for {@link UploadSourceType.UPLOAD} and a media ref for
    * {@link UploadSourceType.LOCAL_MEDIA} or {@link UploadSourceType.EUDIW}.
     *
     * Incomplete files must never be sent to the server: it dereferences exactly those
     * properties without checking them ({@code HisSelectFiles.toUploadFieldInputStreamResource()}
     * calls {@code loadByMediaReference()} for a local-media selection), so a missing ref ends in an
     * {@code IllegalArgumentException} instead of an empty result.
     */
    protected readonly resolvable = computed<boolean>( () => {
        const file = this.file();

        switch ( file?.sourceType ) {
            case UploadSourceType.UPLOAD:
                return file.upload != null;
            case UploadSourceType.LOCAL_MEDIA:
                return !isBlank( file.localMedia?.mediaReference );
            case UploadSourceType.EUDIW:
                return !isBlank( file.eudiwMedia?.mediaReference );
            default:
                return false;
        }
    } );

    /**
     * The comment of this file, or `null` when none is set. Falls back to the comment of
     * the wrapped upload, which is where the server delivers the persisted comment.
     */
    protected readonly comment = computed<string | null>( () => {
        const file = this.file();
        return file?.comment ?? file?.upload?.comment ?? null;
    } );

    /** `true` when this file already carries a non-blank comment. */
    protected readonly hasComment = computed<boolean>( () => !isBlank( this.comment() ) );

    /**
     * `true` when the user may edit the comment of this file, mirroring the JSF
     * {@code isEditCommentVisible()}.
     */
    protected readonly editCommentVisible = computed<boolean>( () =>
        !this.disabled() && !this.readonly() && this.allowComment()
    );

    /**
     * `true` when the existing comment is only displayed (not editable), mirroring
     * the JSF {@code isShowCommentVisible()}.
     */
    protected readonly showCommentVisible = computed<boolean>( () =>
        ( this.disabled() || this.readonly() ) && this.hasComment()
    );

    /** Title of the comment action, mirroring the JSF {@code getCommentIconTitle()}. */
    protected readonly commentIconTitle = computed<string>( () => {
        const filename = this.filename();

        if ( this.disabled() || this.readonly() ) {
            return this.hisUiService.translate( this.TRANSLATION_COMMENT_SHOW, filename );
        }

        if ( !this.hasComment() ) {
            return this.hisUiService.translate( this.TRANSLATION_COMMENT_ADD, filename );
        }

        return this.hisUiService.translate( this.TRANSLATION_COMMENT_EDIT, filename );
    } );

    /** Header of the comment dialog, mirroring the JSF {@code getCommentPopupTitle()}. */
    protected readonly commentDialogTitle = computed<string>( () =>
        this.hisUiService.translate( this.TRANSLATION_COMMENT_OVERLAY_TITLE, this.filename() )
    );

    /**
     * Optional info text rendered above the comment input; empty when not customized.
     *
     * This and the texts below are resolved once while this component is being created,
     * which is safe: the application only starts to render after {@code HisBootstrapService}
     * has loaded the texts of the user's language, and a change of that language reloads the
     * whole page.
     */
    protected readonly commentHelptextAbove = this.hisUiService.translateOrEmpty( 'common.components.selectFile.comment.helptextAbove' );

    /** Optional info text rendered below the comment input; empty when not customized. */
    protected readonly commentHelptextBelow = this.hisUiService.translateOrEmpty( 'common.components.selectFile.comment.helptextBelow' );

    /** Label of the comment input in the comment dialog. */
    protected readonly commentLabel = this.hisUiService.translate( 'common.components.selectFile.comment' );

    /** Helptext of the comment input in the comment dialog; empty when not customized. */
    protected readonly commentHelptext = this.hisUiService.translateOrEmpty( 'common.components.selectFile.comment.helptext' );

    /** Controls the visibility of the comment dialog. */
    protected readonly showCommentDialog = signal( false );

    /**
     * Draft value bound to the textarea of the comment dialog. Initialized whenever the
     * dialog opens and discarded when the dialog is cancelled.
     */
    protected readonly commentDraft = signal( '' );

    /**
     * Resource properties for this file, grouped by category key. `null` when
     * {@link processProperties} is disabled, the file cannot be resolved by the server
     * (see {@link resolvable}) or the request failed.
     *
     * Recomputed only when the underlying file changes (compared via {@link hisSelectFileKey}).
     */
    protected readonly resourceProperties = toSignal<Map<string, ResourcePropertyDetail[]> | null>(
        toObservable( computed( () =>
            !this.processProperties() || !this.resolvable()
                ? null
                : {
                    configKey: this.configKey(),
                    // TODO: add javascriptContext
                    // TODO: add contextObject
                    selectFile: this.file(),
                }
        ) ).pipe(
            distinctUntilChanged( ( a, b ) =>
                a == null || b == null
                    ? a === b
                    : a.configKey === b.configKey && hisSelectFileKey( a.selectFile ) === hisSelectFileKey( b.selectFile )
            ),
            switchMap( params =>
                params == null
                    ? of( null )
                    : this.selectFileService.provideResourceProperties( params ).pipe(
                        catchError( () => of( null ) )
                    )
            )
        ),
        { initialValue: null }
    );

    /**
     * Resource properties presented inside the properties overlay by this component — every
     * category the projected {@link propertyCustomDisplay} does not take care of.
     * `null` when no resource properties are available.
     */
    protected readonly overlayProperties = computed<Map<string, ResourcePropertyDetail[]> | null>( () =>
        this.partitionProperties( ( categoryKey ) =>
            this.displays.showInOverlay( categoryKey ) && !this.customDetailsCategory( categoryKey )
        )
    );

    /**
     * Resource properties presented as an icon next to the filename by this component —
     * every category with a registered icon the projected {@link propertyCustomDisplayIcon}
     * does not take care of. `null` when no resource properties are available.
     */
    protected readonly iconProperties = computed<Map<string, ResourcePropertyDetail[]> | null>( () =>
        this.partitionProperties( ( categoryKey ) =>
            this.displays.iconOf( categoryKey ) != null
            && !( this.propertyCustomDisplayIcon() != null && this.customCategories().has( categoryKey ) )
        )
    );

    /**
     * Resource properties whose category key <em>is</em> listed in
     * {@link propertyCustomDisplayCategories}, handed to the projected icon template.
     * `null` when no resource properties are available.
     */
    protected readonly customProperties = computed<Map<string, ResourcePropertyDetail[]> | null>( () =>
        this.partitionProperties( ( categoryKey ) => this.customCategories().has( categoryKey ) )
    );

    /**
     * Custom properties handed to the projected details template, excluding categories the
     * display registry presents exclusively as an icon.
     */
    protected readonly customOverlayProperties = computed<Map<string, ResourcePropertyDetail[]> | null>( () =>
        this.partitionProperties( ( categoryKey ) =>
            this.displays.showInOverlay( categoryKey ) && this.customCategories().has( categoryKey )
        )
    );

    /** `true` while the properties overlay has anything to show. */
    protected readonly hasOverlayContent = computed<boolean>( () =>
        ( this.overlayProperties()?.size ?? 0 ) > 0
        || ( this.propertyCustomDisplay() != null && ( this.customOverlayProperties()?.size ?? 0 ) > 0 )
    );

    /** The category keys the consumer wants to present itself, as a set for quick lookup. */
    private readonly customCategories = computed<Set<string>>( () => new Set( this.propertyCustomDisplayCategories() ) );

    /** `true` when the given category is presented by the projected details template. */
    private customDetailsCategory( categoryKey: string ): boolean {
        return this.propertyCustomDisplay() != null && this.customCategories().has( categoryKey );
    }

    /**
     * The categories of {@link resourceProperties} the given predicate accepts, or `null` when
     * no resource properties exist.
     */
    private partitionProperties( accept: ( categoryKey: string ) => boolean ): Map<string, ResourcePropertyDetail[]> | null {
        const properties = this.resourceProperties();
        if ( properties == null ) {
            return null;
        }

        const result = new Map<string, ResourcePropertyDetail[]>();
        for ( const [categoryKey, details] of properties ) {
            if ( accept( categoryKey ) ) {
                result.set( categoryKey, details );
            }
        }

        return result;
    }

    /** `true` when the given category is presented by a built-in display in the overlay. */
    protected hasDetailsDisplay( categoryKey: string ): boolean {
        return this.displays.detailsOf( categoryKey ) != null;
    }

    /**
     * The element the focus belongs on once this file has been picked: its download link, or its
     * first usable action while the file offers no download. `null` when it can take no focus.
     */
    focusTarget(): HTMLElement | null {
        return this.download()?.button()?.nativeElement
            ?? this.host.nativeElement.querySelector<HTMLElement>( 'button:not([disabled])' );
    }

    /**
     * Comparator for the {@code keyvalue} pipe keeping the insertion order of the properties map.
     *
     * Without it the pipe would sort the categories alphabetically by their key, discarding the
     * order by sortorder the {@link HisSelectFileService} established.
     */
    protected readonly keepCategoryOrder = (): number => 0;

    protected onRemove(): void {
        this.remove.emit();
    }

    /** Opens the comment dialog with a draft initialized from the current comment. */
    protected openCommentDialog(): void {
        this.commentDraft.set( this.comment() ?? '' );
        this.showCommentDialog.set( true );
    }

    /** Applies the drafted comment and closes the dialog. */
    protected applyComment(): void {
        this.commentChange.emit( this.commentDraft() );
        this.showCommentDialog.set( false );
    }

    /** Discards the drafted comment and closes the dialog. */
    protected cancelComment(): void {
        this.commentDraft.set( '' );
        this.showCommentDialog.set( false );
    }
}
